// This code is related to Plotly.js and provides utility functions
// for configuring the layout of legends in Plotly charts.
// It includes functions to get the legend layout based on its position
// (above or below the chart) and to adjust the margins to avoid
// overlapping with other chart elements.

// Configuration for the legend when it is positioned above the chart
/**
 * Generates the layout configuration for a legend positioned above the chart.
 * @param {number} [yAbove=1.3] - The vertical position of the legend above the chart.
 *                                Default value is 1.3, which places the legend above the chart.
 * @returns {object} - The layout configuration for the legend above the chart.
 */
const legendAbove = (yAbove = 1.3) => {
  return {
    orientation: "h", // Set the orientation of the legend to horizontal
    yanchor: "bottom", // Anchor the legend to the bottom of its position
    y: yAbove, // Position the legend above the chart
    xanchor: "center", // Center the legend horizontally
    x: 0.5, // Center the legend at the midpoint of the chart width
  };
};

// Configuration for the legend when it is positioned below the chart
/**
 * Generates the layout configuration for a legend positioned below the chart.
 * @param {number} [yBelow=-0.3] - The vertical position of the legend below the chart.
 *                                  Default value is -0.3, which places the legend below the chart.
 * @returns {object} - The layout configuration for the legend below the chart.
 */
const legendBelow = (yBelow = -0.3) => {
  return {
    orientation: "h", // Set the orientation of the legend to horizontal
    yanchor: "top", // Anchor the legend to the top of its position
    y: yBelow, // Position the legend below the chart
    xanchor: "center", // Center the legend horizontally
    x: 0.5, // Center the legend at the midpoint of the chart width
  };
};

/**
 * Returns the appropriate legend layout based on its position.
 * @param {boolean} isLegendAbove - Indicates if the legend should be above the chart.
 * @param {number} [yAbove=1.3] - The vertical position of the legend above the chart.
 * @param {number} [yBelow=-0.3] - The vertical position of the legend below the chart.
 * @returns {object} - The layout configuration for the legend.
 */
export const getLegendLayout = (isLegendAbove, yAbove = 1.3, yBelow = -0.3) => {
  return isLegendAbove ? legendAbove(yAbove) : legendBelow(yBelow); // Return the corresponding legend layout
};

/**
 * Returns the margin layout required to avoid overlapping with the plot title
 * and the mode bar based on the legend's position.
 * @param {boolean} isLegendAbove - Indicates if the legend is above the chart.
 * @param {number} [modeBarHeight=100] - The height of the mode bar, default is 100.
 * @returns {object} - The margin configuration for the chart layout.
 */
export const getMarginLayoutRequiredByLegend = (isLegendAbove, modeBarHeight = 100) => {
  return {
    t: isLegendAbove ? modeBarHeight + 50 : 50, // Adjust top margin based on legend position
    b: 50, // Bottom margin
    l: 50, // Left margin
    r: 50, // Right margin
  };
};
